import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate } from "../lib/api";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, Check, Clock, Calendar } from "lucide-react";

const bucketMeta = [
  { key: "overdue", label: "Overdue", icon: AlertTriangle, cls: "text-rose-600" },
  { key: "today", label: "Due Today", icon: Clock, cls: "text-amber-600" },
  { key: "upcoming", label: "Upcoming", icon: Calendar, cls: "text-blue-600" },
  { key: "no_due", label: "No Due Date", icon: Clock, cls: "text-slate-500" },
];

export default function ImmigrationMyTasks() {
  const [data, setData] = useState(null);

  const load = async () => { const r = await api.get("/immigration/my-tasks"); setData(r.data); };
  useEffect(() => { load(); }, []);

  const setStatus = async (t, status) => {
    try { await api.patch(`/immigration/tasks/${t.id}`, { status }); toast.success("Updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!data) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="heading-font text-3xl font-bold text-slate-900">My Tasks</h1>
        <p className="text-sm text-slate-500 mt-1">{data.total} open task{data.total !== 1 ? "s" : ""}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {bucketMeta.map(b => (
          <div key={b.key} className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2"><b.icon className={`w-4 h-4 ${b.cls}`}/><span className="text-xs uppercase text-slate-500">{b.label}</span></div>
            <div className={`heading-font text-2xl font-bold mt-1 ${b.cls}`}>{(data.buckets[b.key]||[]).length}</div>
          </div>
        ))}
      </div>

      {bucketMeta.map(b => {
        const rows = data.buckets[b.key] || [];
        if (rows.length === 0) return null;
        return (
          <div key={b.key} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className={`px-5 py-3 border-b border-slate-200 heading-font font-semibold ${b.cls}`}>{b.label} ({rows.length})</div>
            <div className="divide-y divide-slate-100">
              {rows.map(t => (
                <div key={t.id} data-testid={`imm-task-${t.id}`} className="flex items-start justify-between px-5 py-3 gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{t.title}</div>
                    <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-2">
                      {t.application_id && <Link to={`/immigration/applications/${t.application_id}`} className="text-orange-600 hover:underline mono">{t.application_number}</Link>}
                      {t.client_name && <span>· {t.client_name}</span>}
                      {t.app_type_name && <span>· {t.app_type_name}</span>}
                      {t.due_date && <span>· due {fmtDate(t.due_date)}</span>}
                      {t.priority && <span className={`stage-badge ${t.priority==="URGENT"?"bg-rose-50 text-rose-800 border-rose-200":"bg-slate-50 text-slate-700 border-slate-200"}`}>{t.priority}</span>}
                      {t.auto_generated && <span className="stage-badge bg-blue-50 text-blue-700 border-blue-200">AUTO</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={t.status} onValueChange={v => setStatus(t, v)}>
                      <SelectTrigger className="h-8 w-40"><SelectValue/></SelectTrigger>
                      <SelectContent>{["OPEN","IN PROGRESS","COMPLETED"].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button data-testid={`imm-task-done-${t.id}`} size="sm" variant="outline" onClick={()=>setStatus(t, "COMPLETED")}><Check className="w-4 h-4"/></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {data.total === 0 && <div className="bg-white p-8 rounded-xl border border-slate-200 text-center text-slate-500">No open tasks. 🎉</div>}
    </div>
  );
}
